import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CustomerSummaryDto } from '@bhops/shared';
import { customersApi, type UpdateCustomerBody } from '@/api/customers';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Textarea } from '@/components/Form';
import { EditableSelect } from '@/components/EditableSelect';
import { useToast } from '@/components/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
  customer: CustomerSummaryDto;
}

export function CustomerEditModal({ open, onClose, customer }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<UpdateCustomerBody>(() => dtoToForm(customer));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(dtoToForm(customer));
      setError(null);
    }
  }, [open, customer]);

  const update = useMutation({
    mutationFn: () => customersApi.update(customer.id, form),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      // R2 후속(워크플로 M14) — 상호/영업코드/세그먼트/활성 등은 customer-master 합성
      // row 에도 노출되므로, 마스터 시트·상세가 stale 하지 않도록 함께 무효화.
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      toast.success(`${c.displayName} 정보를 저장했습니다.`, '저장 완료');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    update.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${customer.displayName} 고객사 편집`}
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="customer-edit-form" disabled={update.isPending}>
            {update.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <form id="customer-edit-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <Field label="상호 (브랜드명)" htmlFor="ce-display" required>
          <Input
            id="ce-display"
            value={form.displayName ?? ''}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            required
          />
        </Field>

        <Field label="법인명" htmlFor="ce-legal" required>
          <Input
            id="ce-legal"
            value={form.legalName ?? ''}
            onChange={(e) => setForm({ ...form, legalName: e.target.value })}
            required
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="고객 유형" htmlFor="ce-segment">
            <EditableSelect
              id="ce-segment"
              groupKey="customer.segment"
              value={form.segment ?? ''}
              onChange={(v) => setForm({ ...form, segment: v })}
              placeholder="— 선택 —"
            />
          </Field>
          <Field label="국가 코드" htmlFor="ce-country">
            <Input
              id="ce-country"
              value={form.countryCode ?? 'KR'}
              maxLength={2}
              onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
        </div>

        {/* 오너 지적(2026-09-01) — 이 칸이 자유 입력 + 4글자였다.
            목록에는 이미 `DYLAN`(5글자)처럼 긴 값이 들어와 있어, **손대지 않은 이 칸
            때문에** 비고만 고치려는 저장이 통째로 거부됐다("salesOwnerCode must be
            shorter than or equal to 4 characters"). 같은 칸을 고객사 마스터 창은
            목록에서 고르게 하고 16글자를 허용하고 있었다 — 한 칸에 규칙이 셋이었다.
            정본을 마스터 창 쪽으로 맞춘다(같은 EditableSelect · 같은 목록 키). */}
        <Field label="영업 담당" htmlFor="ce-sales">
          <EditableSelect
            id="ce-sales"
            groupKey="customer.salesOwnerCode"
            value={form.salesOwnerCode ?? ''}
            onChange={(v) => setForm({ ...form, salesOwnerCode: v })}
            placeholder="— 선택 —"
          />
        </Field>

        <Field label="활성 여부" htmlFor="ce-active">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              id="ce-active"
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            활성 고객사
          </label>
        </Field>

        <Field label="비고" htmlFor="ce-notes">
          <Textarea
            id="ce-notes"
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
            rows={3}
          />
        </Field>
      </form>
    </Modal>
  );
}

function dtoToForm(c: CustomerSummaryDto): UpdateCustomerBody {
  return {
    legalName: c.legalName,
    displayName: c.displayName,
    segment: c.segment,
    salesOwnerCode: c.salesOwnerCode ?? undefined,
    countryCode: c.countryCode,
    active: c.active,
    notes: (c as { notes?: string | null }).notes ?? null,
  };
}
